(() => {
  'use strict';
  if (customElements.get('lux-dcc-row')) return;

  class LuxDiamondCubanRow extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.rail = this.querySelector('[data-lux-dcc-rail]');
      this.cards = [...this.querySelectorAll('[data-lux-dcc-card]')];
      if (!this.rail || !this.cards.length) return;
      this.prev = this.querySelector('[data-lux-dcc-prev]');
      this.next = this.querySelector('[data-lux-dcc-next]');
      this.motion = this.querySelector('[data-lux-dcc-motion]');
      this.position = this.querySelector('[data-lux-dcc-position]');
      this.controls = this.querySelector('[data-lux-dcc-controls]');
      this.cleanup = [];
      this.interval = Math.max(5, Math.min(20, Number(this.dataset.interval) || 8)) * 1000;
      this.paused = this.dataset.autoplay !== 'true';
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.onScreen = false;
      this.cooldownUntil = 0;
      this.ignoreScrollUntil = 0;
      this.suppressClickUntil = 0;
      this.pointer = null;
      this.reducedMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced = this.reducedMedia.matches;
      this.measure();

      this.listen(this.prev, 'click', () => this.step(-1));
      this.listen(this.next, 'click', () => this.step(1));
      this.listen(this.motion, 'click', () => {
        if (this.reduced || !this.maxScroll) return;
        this.cancelAnimation();
        if (this.atEnd()) {
          this.paused = false;
          this.moveTo(0);
        } else {
          this.paused = !this.paused;
          this.refresh();
        }
      });
      this.listen(this, 'pointerenter', (event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.hovered = true;
        this.hold();
      });
      this.listen(this, 'pointerleave', () => {
        this.hovered = false;
        this.refresh();
      });
      this.listen(this, 'focusin', () => {
        this.focused = true;
        this.hold();
      });
      this.listen(this, 'focusout', (event) => {
        this.focused = !!event.relatedTarget && this.contains(event.relatedTarget);
        this.refresh();
      });
      this.listen(this.rail, 'keydown', (event) => {
        // Product links and page-level keys retain their normal behavior.
        if (event.target !== this.rail || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const actions = { ArrowLeft: () => this.step(-1), ArrowRight: () => this.step(1), Home: () => this.moveTo(0), End: () => this.moveTo(this.maxScroll) };
        if (!actions[event.key]) return;
        event.preventDefault();
        this.interact();
        actions[event.key]();
      });
      this.listen(this.rail, 'wheel', () => this.interact(), { passive: true });
      this.listen(this.rail, 'scroll', () => {
        if (!this.animation && performance.now() > this.ignoreScrollUntil) {
          this.cooldownUntil = performance.now() + this.interval;
          this.schedule();
        }
        this.render();
      }, { passive: true });
      this.listen(this.rail, 'pointerdown', (event) => this.pointerDown(event), { passive: true });
      this.listen(window, 'pointermove', (event) => this.pointerMove(event), { passive: false });
      this.listen(window, 'pointerup', (event) => this.pointerEnd(event));
      this.listen(window, 'pointercancel', (event) => this.pointerEnd(event));
      this.listen(this.rail, 'lostpointercapture', (event) => this.pointerEnd(event));
      this.listen(this.rail, 'dragstart', (event) => event.preventDefault());
      this.listen(this.rail, 'click', (event) => {
        if (event.detail !== 0 && performance.now() < this.suppressClickUntil) {
          this.suppressClickUntil = 0;
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true });
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.hold();
        else this.refresh();
      });
      this.listen(this.reducedMedia, 'change', (event) => {
        this.reduced = event.matches;
        this.hold();
        this.schedule();
      });

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.onScreen = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          if (!this.onScreen) this.hold();
          else this.refresh();
        }, { threshold: [0, 0.15] });
        this.intersectionObserver.observe(this);
      } else {
        const visibility = () => {
          const bounds = this.getBoundingClientRect();
          this.onScreen = bounds.bottom > 0 && bounds.top < window.innerHeight;
          if (!this.onScreen) this.hold();
          else this.refresh();
        };
        this.listen(window, 'scroll', visibility, { passive: true });
        this.listen(window, 'resize', visibility, { passive: true });
        visibility();
      }
      const resize = () => {
        this.cancelAnimation();
        this.measure();
        this.refresh();
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(resize);
        this.resizeObserver.observe(this.rail);
        this.cards.forEach((card) => this.resizeObserver.observe(card));
      } else this.listen(window, 'resize', resize, { passive: true });

      this.querySelectorAll('[data-lux-dcc-image]').forEach((image) => {
        const fallback = image.closest('[data-lux-dcc-card]')?.querySelector('[data-lux-dcc-fallback]');
        const setFallback = (failed) => {
          if (!fallback) return;
          image.hidden = failed;
          fallback.hidden = !failed;
        };
        this.listen(image, 'error', () => setFallback(true));
        this.listen(image, 'load', () => setFallback(false));
        if (image.complete && image.currentSrc && image.naturalWidth === 0) setFallback(true);
      });
      this.refresh();
    }

    disconnectedCallback() {
      if (!this.cleanup) return;
      clearTimeout(this.timer);
      this.cancelAnimation();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.cleanup.forEach((remove) => remove());
      this.cleanup = null;
      if (this.pointer?.dragging) {
        this.rail.style.scrollSnapType = this.pointer.snap;
        this.rail.style.scrollBehavior = this.pointer.behavior;
      }
      this.pointer = null;
      delete this.dataset.luxDccDragging;
    }

    listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      this.cleanup.push(() => target.removeEventListener(type, handler, options));
    }

    measure() {
      this.maxScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const first = this.cards[0].getBoundingClientRect().left;
      this.offsets = this.cards.map((card) => Math.max(0, card.getBoundingClientRect().left - first));
      this.stops = [...new Set(this.offsets.map((offset) => Math.min(offset, this.maxScroll)))];
    }

    atEnd() { return this.maxScroll > 1 && this.rail.scrollLeft >= this.maxScroll - 2; }

    canPlay() {
      return !this.paused && !this.reduced && !this.hovered && !this.focused && !this.pointer && this.onScreen && !document.hidden && this.maxScroll > 1 && !this.atEnd();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.cleanup || this.animation || !this.canPlay()) return;
      const delay = Math.max(this.interval, this.cooldownUntil - performance.now());
      this.timer = setTimeout(() => {
        if (this.canPlay()) this.step(1, true);
      }, delay);
    }

    render() {
      const end = this.atEnd();
      if (this.controls) this.controls.hidden = this.cards.length < 2;
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.maxScroll <= 1 || end;
      if (this.motion) {
        let label = this.dataset.pauseLabel || 'Pause';
        if (this.reduced) label = this.dataset.reducedLabel || 'Motion off';
        else if (end) label = this.dataset.restartLabel || 'Restart';
        else if (this.paused) label = this.dataset.playLabel || 'Play';
        this.motion.textContent = label;
        this.motion.setAttribute('aria-label', label);
        this.motion.disabled = this.reduced || this.maxScroll <= 1;
      }
      if (this.position) {
        const index = this.offsets.reduce((current, offset, candidate) => offset <= this.rail.scrollLeft + 2 ? candidate : current, 0);
        this.position.textContent = (this.dataset.positionLabel || '{current} of {total}').replace('{current}', index + 1).replace('{total}', this.cards.length);
      }
      this.dataset.luxDccState = this.reduced ? 'reduced' : end ? 'complete' : this.paused ? 'paused' : this.canPlay() ? 'playing' : 'waiting';
    }

    refresh() {
      this.render();
      this.schedule();
    }

    hold() {
      clearTimeout(this.timer);
      this.cancelAnimation();
      this.render();
    }

    interact() {
      this.hold();
      this.cooldownUntil = performance.now() + this.interval;
      this.schedule();
    }

    step(direction, automatic = false) {
      const current = this.rail.scrollLeft;
      const target = direction > 0 ? this.stops.find((stop) => stop > current + 2) : [...this.stops].reverse().find((stop) => stop < current - 2);
      if (target === undefined) return;
      if (!automatic) this.interact();
      this.moveTo(target);
    }

    moveTo(target) {
      this.cancelAnimation();
      clearTimeout(this.timer);
      target = Math.max(0, Math.min(target, this.maxScroll));
      const start = this.rail.scrollLeft;
      const styles = { snap: this.rail.style.scrollSnapType, behavior: this.rail.style.scrollBehavior };
      this.rail.style.scrollSnapType = 'none';
      this.rail.style.scrollBehavior = 'auto';
      this.animation = { styles, started: performance.now(), start, target };
      if (this.reduced || Math.abs(start - target) < 1) {
        this.rail.scrollLeft = target;
        this.cancelAnimation();
        this.refresh();
        return;
      }
      const tick = (now) => {
        if (!this.animation || !this.cleanup) return;
        const progress = Math.min(1, (now - this.animation.started) / 850);
        const eased = 0.5 - Math.cos(progress * Math.PI) / 2;
        this.rail.scrollLeft = start + (target - start) * eased;
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else {
          this.cancelAnimation();
          this.refresh();
        }
      };
      this.frame = requestAnimationFrame(tick);
    }

    cancelAnimation() {
      cancelAnimationFrame(this.frame);
      if (!this.animation) return;
      this.rail.style.scrollSnapType = this.animation.styles.snap;
      this.rail.style.scrollBehavior = this.animation.styles.behavior;
      this.animation = null;
      // Ignore the final programmatic scroll event, not subsequent user input.
      this.ignoreScrollUntil = performance.now() + 100;
    }

    pointerDown(event) {
      if (event.button !== 0 || this.pointer) return;
      this.suppressClickUntil = 0;
      this.interact();
      this.pointer = { id: event.pointerId, type: event.pointerType, x: event.clientX, y: event.clientY, left: this.rail.scrollLeft, dragging: false };
      this.refresh();
    }

    pointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id || pointer.type !== 'mouse') return;
      const x = event.clientX - pointer.x;
      const y = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(x) < 8 && Math.abs(y) < 8) return;
        if (Math.abs(y) > Math.abs(x)) {
          this.pointer = null;
          this.refresh();
          return;
        }
        pointer.dragging = true;
        pointer.snap = this.rail.style.scrollSnapType;
        pointer.behavior = this.rail.style.scrollBehavior;
        this.rail.style.scrollSnapType = 'none';
        this.rail.style.scrollBehavior = 'auto';
        this.dataset.luxDccDragging = 'true';
        this.rail.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      this.rail.scrollLeft = pointer.left - x;
    }

    pointerEnd(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      this.pointer = null;
      if (pointer.dragging) {
        this.rail.style.scrollSnapType = pointer.snap;
        this.rail.style.scrollBehavior = pointer.behavior;
        this.suppressClickUntil = performance.now() + 500;
        delete this.dataset.luxDccDragging;
        if (this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
      }
      this.interact();
      this.render();
    }
  }

  customElements.define('lux-dcc-row', LuxDiamondCubanRow);
})();
